import { useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, Badge, Button, Card, Col, Form, Row } from "react-bootstrap";
import DateTimePicker from 'react-datetime';

import { UserSubscriptionTier } from "../../../../__gqltypes__";
import { applyChangeIfDateValid, validIfSameDayOrAfter } from '../../../../utils/datepicker';

export type EventMatchersConfig = {
  filterSubscriptionTier: UserSubscriptionTier | undefined | null;
  filterUserIds: string | undefined | null;
  filterUserMinCreationDate: Date | undefined | null;
  filterUserMaxCreationDate: Date | undefined | null;
  filterAppVersion: string | undefined | null;
  filterUserLevel: number | undefined | null;
}

export const eventMatcherDefaultConfig: EventMatchersConfig = {
  filterSubscriptionTier: undefined,
  filterUserIds: '',
  filterUserMinCreationDate: undefined,
  filterUserMaxCreationDate: undefined,
  filterAppVersion: undefined,
  filterUserLevel: undefined,
};

type Props = {
  config: EventMatchersConfig;
  onUpdate: (newConfig: EventMatchersConfig | ((prev: EventMatchersConfig) => EventMatchersConfig)) => void;
  // Opt-in (Marathon): accent the collapsed header and summarize the min app version there.
  highlight?: boolean;
}

export function userIdsArrayToString(userIdsArray: string[] | string): string {
  if (Array.isArray(userIdsArray)) {
    return userIdsArray.join(', ');
  }
  return userIdsArray;
}

export function userIdsStringToArray(songIdsString: string): string[] | undefined {
  if (typeof songIdsString === 'string' && songIdsString.trim().length > 0) {
    return songIdsString.split(',').map((id) => id.trim());
  }
  return undefined;
}

export function EventMatchers({config, onUpdate, highlight = false}: Props): JSX.Element {

    const [expanded, setExpanded] = useState(false);
    const [filterSubscriptionTier, setFilterSubscriptionTier] = useState<UserSubscriptionTier | undefined | 'All'>(
      'All'
    );
    const [filterUserIds, setFilterUserIds] = useState<string>('');
    const [filterUserMinCreationDate, setFilterUserMinCreationDate] = useState<Date | undefined>();
    const [filterUserMaxCreationDate, setFilterUserMaxCreationDate] = useState<Date | undefined>();
    const [filterAppVersion, setFilterAppVersion] = useState<string | undefined>();
    const [filterUserLevel, setFilterUserLevel] = useState<number | undefined>();

    const handleChangeAppVersion = useCallback((event) => {
      setFilterAppVersion(event.target.value);
      onUpdate((prev) => ({...prev, filterAppVersion: event.target.value}));
    }, [onUpdate]);

    const handleChangeUserLevel = useCallback((event) => {
      const val = !Number.isNaN(+event.target.value) ? +event.target.value : undefined;
      setFilterUserLevel(val);
      onUpdate((prev) => ({...prev, filterUserLevel: val}));
    }, [onUpdate]);

    const handleChangeSubscriberStatus = useCallback((event) => {
      const val = event.currentTarget.value === 'All' ? undefined : (event.currentTarget.value as UserSubscriptionTier);
      setFilterSubscriptionTier(val);
      onUpdate((prev) => ({...prev, filterSubscriptionTier: val}));
    }, [onUpdate]);

    const handleChangePlayerIds = useCallback((event) => {
      setFilterUserIds(event.target.value);
      onUpdate((prev) => ({...prev,filterUserIds: event.target.value}));
    }, [onUpdate]);

    const handleMinDate = useCallback((value: Date) => {
      setFilterUserMinCreationDate(value);
      onUpdate((prev) => ({...prev, filterUserMinCreationDate: value}));
    }, [onUpdate]);
    
    const handleMaxDate = useCallback((value: Date) => {
      setFilterUserMaxCreationDate(value);
      onUpdate((prev) => ({...prev, filterUserMaxCreationDate: value}));
    }, [onUpdate]);

    useEffect(() => {
      setFilterUserIds(config.filterUserIds ?? '');
      setFilterSubscriptionTier(config.filterSubscriptionTier ?? undefined);
      setFilterUserMinCreationDate((d) => {
        if (!d || d.valueOf() !== config.filterUserMinCreationDate?.valueOf()) {
          return config.filterUserMinCreationDate ?? undefined;
        }
        return d;
      });
      setFilterUserMaxCreationDate((d) => {
        if (!d || d.valueOf() !== config.filterUserMaxCreationDate?.valueOf()) {
          return config.filterUserMaxCreationDate ?? undefined;
        }
        return d;
      });
      setFilterAppVersion(config.filterAppVersion ?? undefined);
      setFilterUserLevel(config.filterUserLevel ?? undefined);
    }, [config]);

    return (
      <Accordion>
        <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(value => !value)}>
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                ...(highlight
                  ? {
                      backgroundColor: '#eef4ff',
                      borderLeft: '4px solid #0d6efd',
                      borderRadius: 4,
                      padding: '8px 12px',
                    }
                  : {}),
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                Matchers
                {highlight && !expanded && (
                  <>
                    <Badge variant="primary" className="ml-2">
                      {config.filterAppVersion ? `App ≥ ${config.filterAppVersion}` : 'App: any version'}
                    </Badge>
                    <small className="text-muted ml-2">Expand for more matcher config</small>
                  </>
                )}
              </span>
            <Button variant="link">
              {!expanded && <i className="fas fa-chevron-down" />}
              {expanded && <i className="fas fa-chevron-up" />}
            </Button>
          </div>
        </Accordion.Toggle>
        <Accordion.Collapse eventKey="0">
          <>
            <Row className="mb-3">
              <Col md="auto">
              <Button variant="danger" onClick={() => onUpdate(eventMatcherDefaultConfig)}>
                <i className="fas fa-recycle" /> Reset
              </Button>
              </Col>
            </Row>
              
            <Row className="mb-3">
              <Col md={3}>Minimum Registration Date:</Col>
              <Col md={5}>
                <DateTimePicker  
                  dateFormat="YYYY-MM-DD"
                  timeFormat={true}
                  timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                  // @ts-ignore
                  onChange={applyChangeIfDateValid(handleMinDate)}
                  // @ts-ignore
                  value={filterUserMinCreationDate ?? undefined}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>Maximum Registration Date:</Col>
              <Col md={5}>
                <DateTimePicker
                  dateFormat="YYYY-MM-DD"
                  timeFormat={true}
                  timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
                  // @ts-ignore
                  onChange={applyChangeIfDateValid(handleMaxDate)}
                  // @ts-ignore
                  value={filterUserMaxCreationDate ?? undefined}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>Min App Version:</Col>
              <Col md={5}>
                <Form.Control type="text" value={filterAppVersion} onChange={handleChangeAppVersion} />
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={3}>Min User Level:</Col>
              <Col md={5}>
                <Form.Control type="text" value={filterUserLevel} onChange={handleChangeUserLevel} />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>User Subscription Status:</Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Control
                    as="select"
                    type="text"
                    value={filterSubscriptionTier}
                    onChange={handleChangeSubscriberStatus}
                  >
                    <option>All</option>
                    {Object.keys(UserSubscriptionTier).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>
                User Ids: <br />
                (separated by commas &quot;,&quot;)
              </Col>
              <Col md={8}>
                <Form.Control
                  as="textarea"
                  value={filterUserIds.toString()}
                  onChange={handleChangePlayerIds}
                />
              </Col>
            </Row>
            </>
          </Accordion.Collapse>
        </Accordion>
    );
}